import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { TerminalIcon, GaugeIcon, CoinsIcon } from 'lucide-react';
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function ProductSlide() {
  return <motion.div className="w-full h-full flex flex-col px-12 py-10" variants={stagger} initial="hidden" animate="visible" data-id="element-255">
      <motion.div variants={fadeUp} className="flex items-center gap-3 mb-2" data-id="element-256">
        <span className="text-base text-[#00d4ff] font-semibold tracking-widest uppercase" data-id="element-257">
          04
        </span>
        <span className="w-8 h-[1px] bg-[#00d4ff] opacity-50" data-id="element-258" />
      </motion.div>

      <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-extrabold text-white mb-3" data-id="element-259">
        Product / Demo
      </motion.h2>

      <motion.p variants={fadeUp} className="text-2xl text-[#00d4ff] font-semibold mb-8" data-id="element-260">
        Live: Run inference on FPGAs in 3 lines of code
      </motion.p>

      <div className="flex-1 flex flex-col md:flex-row gap-6" data-id="element-261">
        {/* Code block */}
        <motion.div variants={fadeUp} className="flex-1 glass-card p-6 font-mono text-sm overflow-hidden" data-id="element-262">
          <div className="flex items-center gap-2 mb-4" data-id="element-263">
            <TerminalIcon className="w-5 h-5 text-[#00d4ff]" data-id="element-264" />
            <span className="text-base text-[#8899aa] uppercase tracking-wider" data-id="element-265">
              API Call
            </span>
          </div>
          <div className="space-y-1 text-[17px] leading-7" data-id="element-266">
            <p data-id="element-267">
              <span className="text-[#ff6b35]" data-id="element-268">import</span>{' '}
              <span className="text-white" data-id="element-269">openfpga</span>
            </p>
            <p className="mt-2" data-id="element-270">
              <span className="text-[#8899aa]" data-id="element-271">client</span>{' '}
              <span className="text-[#ff6b35]" data-id="element-272">=</span>{' '}
              <span className="text-white" data-id="element-273">openfpga</span>
              <span className="text-[#8899aa]" data-id="element-274">.</span>
              <span className="text-[#00d4ff]" data-id="element-275">Client</span>
              <span className="text-[#8899aa]" data-id="element-276">(</span>
              <span className="text-green-400" data-id="element-277">api_key</span>
              <span className="text-[#8899aa]" data-id="element-278">)</span>
            </p>
            <p className="mt-2" data-id="element-279">
              <span className="text-[#8899aa]" data-id="element-280">result</span>{' '}
              <span className="text-[#ff6b35]" data-id="element-281">=</span>{' '}
              <span className="text-white" data-id="element-282">client</span>
              <span className="text-[#8899aa]" data-id="element-283">.</span>
              <span className="text-[#00d4ff]" data-id="element-284">infer</span>
              <span className="text-[#8899aa]" data-id="element-285">(</span>
            </p>
            <p className="pl-6" data-id="element-286">
              <span className="text-green-400" data-id="element-287">model</span>
              <span className="text-[#8899aa]" data-id="element-288">=</span>
              <span className="text-amber-300" data-id="element-289">"llama-3-70b"</span>
              <span className="text-[#8899aa]" data-id="element-290">,</span>
            </p>
            <p className="pl-6" data-id="element-291">
              <span className="text-green-400" data-id="element-292">prompt</span>
              <span className="text-[#8899aa]" data-id="element-293">=</span>
              <span className="text-amber-300" data-id="element-294">"Explain FPGAs"</span>
            </p>
            <p data-id="element-295">
              <span className="text-[#8899aa]" data-id="element-296">)</span>
            </p>
          </div>
          <div className="mt-6 pt-4 border-t border-[rgba(255,255,255,0.06)]" data-id="element-297">
            <p className="text-base text-[#8899aa]" data-id="element-298">
              Screenshot of API call or dashboard showing model upload and
              inference request
            </p>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="md:w-64 flex flex-col gap-5" data-id="element-299">
          <motion.div variants={fadeUp} className="glass-card p-5 flex items-start gap-3 glow-orange" data-id="element-300">
            <GaugeIcon className="w-5 h-5 text-[#ff6b35] mt-0.5 flex-shrink-0" data-id="element-301" />
            <div data-id="element-302">
              <p className="text-2xl font-bold text-[#ff6b35]" data-id="element-303">2-10X</p>
              <p className="text-lg text-[#8899aa]" data-id="element-304">
                Cheaper than GPU tokens/$
              </p>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="glass-card p-5 flex items-start gap-3 glow-cyan" data-id="element-305">
            <GaugeIcon className="w-5 h-5 text-[#00d4ff] mt-0.5 flex-shrink-0" data-id="element-306" />
            <div data-id="element-307">
              <p className="text-2xl font-bold text-[#00d4ff]" data-id="element-308">2-5X</p>
              <p className="text-lg text-[#8899aa]" data-id="element-309">Faster than GPU tokens/$</p>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="glass-card p-5 flex items-start gap-3" data-id="element-310">
            <CoinsIcon className="w-5 h-5 text-[#00d4ff] mt-0.5 flex-shrink-0" data-id="element-311" />
            <div data-id="element-312">
              <p className="text-xl font-semibold text-white" data-id="element-313">
                Customer value proposition:
              </p>
              <p className="text-lg text-[#00d4ff] font-medium mt-1" data-id="element-314">
                "More tokens for less."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}